import * as React from "react";

import { searchLocations, setLocationOnServerSession } from "../locationRepo";

import { LocationResults } from "./LocationResults";
import { LocationSearchForm, searchIsNotEmpty } from "./LocationSearchForm";

// Combines the search form and results and
// controls the relationship between the two
export function LocationSearchComponent({
  handleSelect,
}: {
  handleSelect: (item: SimpleLocation) => void;
}) {
  const [locations, setLocations] = React.useState<Array<SimpleLocation>>([]);
  const [params, setParams] = React.useState<LocationSearchParams>({
    FacilityType: 0,
    StateSearchTerm: "",
    CitySearchTerm: "",
    NameSearchTerm: "",
  });
  const [hasSearch, setHasSearch] = React.useState(false);
  const [showScrollHint, setShowScrollHint] = React.useState(false);

  const debounceTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestParamsKey = React.useRef<string>("");
  const resultsRef = React.useRef<HTMLDivElement>(null);
  const observer = React.useRef<IntersectionObserver | null>(null);

  function handleSearchChanges(newParams: LocationSearchParams): void {
    setParams(newParams);

    if (debounceTimer.current !== null) {
      clearTimeout(debounceTimer.current);
    }

    const paramsKey = JSON.stringify(newParams);
    latestParamsKey.current = paramsKey;

    debounceTimer.current = setTimeout(async () => {
      if (latestParamsKey.current !== paramsKey) return;

      let results: Array<SimpleLocation> = [];
      let newHasSearch = false;

      if (searchIsNotEmpty(newParams)) {
        results = await searchLocations(newParams);
        if (latestParamsKey.current !== paramsKey) return;
        newHasSearch = true;
      }

      results.sort((a, b) => {
        const nameA = a.Name;
        const nameB = b.Name;
        if (nameA < nameB) {
          return -1;
        } else if (nameA > nameB) {
          return 1;
        } else {
          return 0;
        }
      });

      setLocations(results);
      setHasSearch(newHasSearch);
    }, 400);
  }

  React.useEffect(() => {
    if (!hasSearch) {
      setShowScrollHint(false);
    }
  }, [hasSearch]);

  React.useEffect(() => {
    if (locations.length === 0) return;

    requestAnimationFrame(() => {
      if (!resultsRef.current) return;

      const rect = resultsRef.current.getBoundingClientRect();
      if (rect.bottom > window.innerHeight) {
        setShowScrollHint(true);

        if (observer.current) {
          observer.current.disconnect();
          observer.current = null;
        }
        observer.current = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              setShowScrollHint(false);
              if (observer.current) {
                observer.current.disconnect();
                observer.current = null;
              }
            }
          },
          { threshold: 0.1 }
        );
        observer.current.observe(resultsRef.current);
      }
    });
  }, [locations]);

  React.useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  async function handleSelectItem(item: SimpleLocation) {
    await setLocationOnServerSession(item.ID);
    handleSelect(item);
  }

  const hasResults = locations.length > 0;

  return (
    <>
      <LocationSearchForm
        params={params}
        onSearchChange={handleSearchChanges}
      />

      {hasSearch && (
        <p className="result-count">
          {(() => {
            const terms = [params.NameSearchTerm || params.CitySearchTerm, params.StateSearchTerm]
              .filter(Boolean)
              .join(", ");
            const label = terms ? `for "${terms}"` : "";
            return hasResults
              ? `${locations.length} location${locations.length !== 1 ? "s" : ""} found ${label}`
              : `No results found${label ? ` ${label}` : ""}. Try: using fewer words, checking your spelling, or searching by city/state instead.`;
          })()}
        </p>
      )}
      {showScrollHint && <div className="scroll-hint">↓ Scroll to see results</div>}
      {hasResults && (
        <div ref={resultsRef}>
          <LocationResults
            results={locations}
            handleSelect={handleSelectItem}
            searchTerm={params.NameSearchTerm}
          />
        </div>
      )}
    </>
  );
}
